import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { twsAPI, portfolioAPI } from '../services/api';

const PortfolioContext = createContext(null);

// Durée de validité du cache (60 secondes - augmenté pour réduire les rechargements)
const CACHE_DURATION_MS = 60000;
// Intervalle de vérification périodique (30 secondes - réduit pour éviter les appels trop fréquents)
const REFRESH_INTERVAL_MS = 30000;

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};

export const PortfolioProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  
  // Verrous pour éviter les appels simultanés
  const isLoadingRef = useRef(false);
  const isCheckingStatusRef = useRef(false);
  // Référence pour préserver les données même en cas d'erreur temporaire
  const cachedDataRef = useRef({ account: null, positions: [], timestamp: null });

  // Vérifier le statut de connexion (avec verrou pour éviter les appels simultanés)
  const checkStatus = useCallback(async () => {
    // Si une vérification est déjà en cours, retourner le statut actuel
    if (isCheckingStatusRef.current) {
      return isConnected;
    }
    
    isCheckingStatusRef.current = true;
    try {
      const response = await twsAPI.getStatus();
      const connected = response.data?.is_connected ?? false;
      // Logs de débogage désactivés
      setIsConnected(connected);
      return connected;
    } catch (err) {
      console.warn('⚠️ [TWS Status Check] Erreur lors de la vérification:', {
        status: err.response?.status,
        message: err.message,
        code: err.code
      });
      
      // Si erreur 503, TWS n'est pas connecté
      if (err.response?.status === 503) {
        setIsConnected(false);
        return false;
      }
      
      if (err.response?.status === 504) {
        return isConnected;
      }
      
      if (err.code === 'ERR_NETWORK' || err.message?.includes('Network')) {
        return isConnected; // Retourner le statut actuel
      }
      
      // Seulement changer le statut en cas d'erreur critique (404, 500, etc.)
      // Mais seulement si on était connecté avant (pour éviter de marquer comme déconnecté si on ne l'a jamais été)
      if (isConnected && err.response?.status !== 200) {
        console.warn('⚠️ [TWS Status Check] Erreur critique, passage à déconnecté');
        setIsConnected(false);
        return false;
      }
      
      // Si on n'était pas connecté et qu'il y a une erreur, garder le statut
      return isConnected;
    } finally {
      isCheckingStatusRef.current = false;
    }
  }, [isConnected]);

  // Charger les données du compte (préserve les données en cache en cas d'erreur)
  const loadAccount = useCallback(async () => {
    try {
      const accountRes = await twsAPI.getAccount();
      const accountData = accountRes.data;
      setAccount(accountData);
      cachedDataRef.current.account = accountData;
      setError(null);
      return accountData;
    } catch (err) {
      console.error('🔴 [loadAccount] Erreur détaillée:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        code: err.code,
        url: err.config?.url
      });
      
      // Ne pas effacer les données en cas d'erreur temporaire
      // Utiliser les données en cache si disponibles
      if (cachedDataRef.current.account) {
        setAccount(cachedDataRef.current.account);
      }
      // Ne définir l'erreur que si c'est une erreur critique
      if (err.response?.status === 404) {
        const url = err.config?.url || 'route inconnue';
        setError(`Route API non trouvée (404): ${url}. Vérifiez que le backend est démarré.`);
      } else if (err.response?.status !== 503 && err.response?.status !== 504 && err.code !== 'ERR_NETWORK') {
        setError(err.message);
      }
      return cachedDataRef.current.account;
    }
  }, []);

  // Charger les positions (TWS live, puis secours cache Redis 5 min si TWS indisponible)
  const loadPositions = useCallback(async () => {
    try {
      const positionsRes = await twsAPI.getPositions();
      const positionsData = positionsRes.data || [];
      setPositions(positionsData);
      cachedDataRef.current.positions = positionsData;
      setError(null);
      return positionsData;
    } catch (err) {
      console.error('🔴 [loadPositions] Erreur détaillée:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        code: err.code,
        url: err.config?.url
      });

      // Secours : positions depuis le cache Redis (Oliver, TTL 5 min) pour toujours avoir quelque chose à afficher
      const isTwsUnavailable = err.response?.status === 503 || err.response?.status === 504 || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED';
      if (isTwsUnavailable) {
        try {
          const cacheRes = await portfolioAPI.getCachedPositions();
          const cachedList = cacheRes.data?.positions || [];
          if (cachedList.length > 0) {
            setPositions(cachedList);
            cachedDataRef.current.positions = cachedList;
            setError(null);
            return cachedList;
          }
        } catch (cacheErr) {
          console.warn('⚠️ [loadPositions] Cache portefeuille indisponible:', cacheErr?.message);
        }
      }

      // Sinon : garder les dernières données en mémoire si disponibles
      if (cachedDataRef.current.positions && cachedDataRef.current.positions.length > 0) {
        setPositions(cachedDataRef.current.positions);
      } else {
        setPositions([]);
      }
      if (err.response?.status === 404) {
        const url = err.config?.url || 'route inconnue';
        setError(`Route API non trouvée (404): ${url}. Vérifiez que le backend est démarré.`);
      } else if (err.response?.status !== 503 && err.response?.status !== 504 && err.code !== 'ERR_NETWORK') {
        setError(err.message);
      }
      return cachedDataRef.current.positions || [];
    }
  }, []);

  // Charger toutes les données (compte + positions) avec verrou pour éviter les appels simultanés
  const loadData = useCallback(async (force = false, silent = false) => {
    // Vérifier si un chargement est déjà en cours
    if (isLoadingRef.current) {
      return;
    }

    // Vérifier si le cache est encore valide
    const now = Date.now();
    if (!force && lastUpdate && (now - lastUpdate) < CACHE_DURATION_MS) {
      return;
    }

    isLoadingRef.current = true;

    // Si c'est un rechargement silencieux en arrière-plan, utiliser backgroundLoading
    if (silent && lastUpdate) {
      setBackgroundLoading(true);
    } else {
      setLoading(true);
    }

    try {
      // Vérifier le statut de connexion (sans changer le statut en cas d'erreur temporaire)
      const connected = await checkStatus();
      
      // Si pas connecté, essayer de se connecter (une seule fois)
      if (!connected) {
        try {
          await twsAPI.connect();
          // Attendre un peu pour que la connexion s'établisse
          await new Promise(resolve => setTimeout(resolve, 500));
          const statusAfterConnect = await checkStatus();
          if (!statusAfterConnect) {
            // Ne pas effacer les données, juste retourner
            return;
          }
        } catch (connectErr) {
          // Ne pas effacer les données en cas d'erreur de connexion
          return;
        }
      }

      setIsConnected(true);
      
      // Charger les données en parallèle
      const [accountData, positionsData] = await Promise.all([
        loadAccount(),
        loadPositions()
      ]);
      
      // Mettre à jour le cache seulement si les données ont été chargées avec succès
      const hasAccount = accountData !== null && accountData !== undefined;
      const hasPositions = positionsData && positionsData.length > 0;
      
      if (hasAccount || hasPositions) {
        setLastUpdate(now);
        cachedDataRef.current.timestamp = now;
      }
    } catch (err) {
      console.error('Error loading portfolio data:', err);
      // Ne définir l'erreur que si c'est une erreur critique
      if (err.response?.status !== 504 && err.code !== 'ERR_NETWORK') {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setBackgroundLoading(false);
      isLoadingRef.current = false;
    }
  }, [checkStatus, loadAccount, loadPositions, lastUpdate, account, positions]);

  // Recharger les données (force le rechargement)
  const refreshData = useCallback(async () => {
    await loadData(true);
  }, [loadData]);

  // Connecter à TWS
  const connect = useCallback(async () => {
    setLoading(true);
    try {
      await twsAPI.connect();
      setIsConnected(true);
      await loadData(true);
      return true;
    } catch (err) {
      console.error('❌ [PortfolioContext Connect] Erreur détaillée:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        code: err.code,
        url: err.config?.url
      });
      setIsConnected(false);
      // Propager l'erreur pour que le composant appelant puisse afficher le message détaillé
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadData]);

  // Déconnecter de TWS
  const disconnect = useCallback(async () => {
    setLoading(true);
    try {
      await twsAPI.disconnect();
      setIsConnected(false);
      setAccount(null);
      setPositions([]);
      setLastUpdate(null);
    } catch (err) {
      console.error('Error disconnecting:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Vérifier le statut au démarrage et charger les données
  useEffect(() => {
    const initialize = async () => {
      try {
        const connected = await checkStatus();
        
        if (connected) {
          await loadData(false);
        } else {
          // Si TWS n'est pas connecté, ne pas essayer de charger les données
        }
      } catch (err) {
        // Erreur silencieuse lors de l'initialisation
      }
    };
    
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Seulement au montage

  // Vérifier périodiquement le statut et rafraîchir les données
  useEffect(() => {
    const interval = setInterval(async () => {
      const now = Date.now();
      const shouldRefresh = !lastUpdate || (now - lastUpdate) >= CACHE_DURATION_MS;
      
      if (shouldRefresh && !isLoadingRef.current) {
        // Vérifier le statut périodiquement (toutes les 2 minutes)
        // pour s'assurer qu'il est à jour même si on ne charge pas les données
        const statusCheckInterval = 120000; // 2 minutes
        const lastStatusCheck = localStorage.getItem('lastTWSStatusCheck');
        const shouldCheckStatus = !lastStatusCheck || (now - parseInt(lastStatusCheck)) >= statusCheckInterval;
        
        if (shouldCheckStatus) {
          try {
            const connected = await checkStatus();
            localStorage.setItem('lastTWSStatusCheck', now.toString());
            // Logs de débogage désactivés
            
            if (connected && !isConnected) {
              await loadData(false);
            }
          } catch (err) {
            console.warn('⚠️ [PortfolioContext] Erreur lors de la vérification périodique:', err.message);
          }
        }
        
        // Charger les données si connecté
        if (isConnected) {
          try {
            await loadData(true, true);
          } catch (err) {
            // Ne pas changer le statut en cas d'erreur
            // Mais vérifier si c'est une erreur 503 qui indique une déconnexion
            if (err.response?.status === 503) {
              console.warn('⚠️ [PortfolioContext] Erreur 503 détectée, vérification du statut...');
              // Vérifier le statut après une erreur 503
              setTimeout(async () => {
                try {
                  await checkStatus();
                } catch (statusErr) {
                  // Ignorer les erreurs de vérification
                }
              }, 1000);
            }
          }
        }
      }
    }, REFRESH_INTERVAL_MS); // Vérifier toutes les 30 secondes

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdate, loadData, isConnected, checkStatus]);

  // PHASE 1: Optimisation des re-renders - mémoriser la valeur du context
  const contextValue = useMemo(() => ({
    isConnected,
    account,
    positions,
    loading,
    backgroundLoading,
    error,
    lastUpdate,
    checkStatus,
    loadData,
    refreshData,
    loadAccount,
    loadPositions,
    connect,
    disconnect,
  }), [
    isConnected,
    account,
    positions,
    loading,
    backgroundLoading,
    error,
    lastUpdate,
    checkStatus,
    loadData,
    refreshData,
    loadAccount,
    loadPositions,
    connect,
    disconnect,
  ]);

  return (
    <PortfolioContext.Provider value={contextValue}>
      {children}
    </PortfolioContext.Provider>
  );
};

